import { useEarnDrop } from '@hooks/TRPC_Hooks/useEarnDropMutate'
import { useProfile } from '@hooks/TRPC_Hooks/useProfile'
import { gtagEventTrigger } from '@specs/helpers'
import { drop, DropMechanics, toy } from '@specs/specs'
import { useSession } from 'next-auth/react'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { LinksEnum, PageQueries } from '@specs/navigation'
import { api } from '@api'
import { earnDrop } from '@specs/features/earnDrop'
import { getSanitizedCode } from '@services/toys/helpers'

interface collectHandlerArguments {
    dropId: drop['id']
}

interface Props {
    toyId: toy['id']
}

export const useCollectDrop = ({ toyId }: Props) => {
    const utils = api.useContext()
    const router = useRouter()
    const { status } = useSession()
    const { secret, code }: PageQueries = router.query
    const [idempotencyKey, setIdempotencyKey] = useState<number>(Math.floor(Date.now() / 1000) + Math.floor(Math.random() * 10000))
    const { data: profile, isLoading: profileLoading } = useProfile()
    const isArtClubMember = !!profile?.isArtClubMember

    const isLoading = status === 'authenticated' && profileLoading

    const { mutateAsync: collectDrop } = useEarnDrop(toyId, idempotencyKey, {
        onSuccess: async () => {
            setIdempotencyKey(Math.floor(Date.now() / 1000) + Math.floor(Math.random() * 10000))
        },
    })

    const collectHandler = async ({ dropId }: collectHandlerArguments) => {
        if (isLoading) return
        const drop = await utils.DropManager.getDrop.fetch({ dropId })
        if (drop.status !== 'success') return
        const { mechanic, alternativeCollect } = drop.result

        if (mechanic === DropMechanics.ARTCLUB && !isArtClubMember) {
            gtagEventTrigger({
                action: 'go_to_chikoroko_art',
                category: 'commercial',
            })
            await router.push(LinksEnum.JOIN_ART_CLUB)
            return
        }

        const baseParams = { secret: getSanitizedCode(secret), idempotencyKey } satisfies earnDrop.EarnDefaultDrop
        let uniqueParams = {}
        if (alternativeCollect === 'COMPETITION' && mechanic !== DropMechanics.BUY) {
            const competition = await utils.DropManager.getCompetition.fetch()
            if (competition.status !== 'success') return
            uniqueParams = { competition: { key: competition.result.id, code } } satisfies earnDrop.EarnAlternativeCompetitionDrop
        }

        const params = { ...baseParams, ...uniqueParams }

        return await collectDrop({ dropId, params })
    }

    return { isArtClubMember, isLoading, collectHandler }
}
